export interface NamedTargetingEntity {
  id: string;
  name: string;
}

export enum TargetingLocationType {
  Continent = 'CONTINENT',
  Country = 'COUNTRY',
  Region = 'REGION',
  Subregion = 'SUB_REGION',
  City = 'CITY',
  Zip = 'ZIP',
}

export enum TargetingSupplyType {
  All = 'ALL',
  Apps = 'APPS',
  Domains = 'DOMAINS',
}

export enum TargetingCsvKind {
  Bundles = 'bundles',
  Ips = 'ips',
}

export enum TargetingPlatformCatalog {
  MadBuy = 'MAD_BUY',
  Shigo = 'SHIGO',
}

export interface TargetingDeviceType extends NamedTargetingEntity {
  deviceTypeId: string;
}

export interface PersistedTargetingDeviceType {
  deviceTypeId?: string | undefined;
  id?: string | undefined;
  excluded: boolean;
}

export interface PersistedTargetingPlatform {
  id?: string | undefined;
  platform?: string | undefined;
  excluded: boolean;
  name?: string | undefined;
}

export interface TargetingLanguage extends NamedTargetingEntity {
  code?: string | undefined;
  isoCode2?: string | undefined;
  isoCode3?: string | undefined;
}

export interface TargetingPlacement extends NamedTargetingEntity {
  secondaryId?: string | null | undefined;
  deliveryType?: string | null | undefined;
  bidFloor?: number | null | undefined;
  cpm?: number | null | undefined;
}

export interface TargetingIabCategory extends NamedTargetingEntity {
  code?: string | undefined;
}

export interface TargetingLocation<LocationId = string> extends Record<string, unknown> {
  continentId?: string | undefined;
  countryId?: string | undefined;
  countryName?: string | undefined;
  excluded: boolean;
  type: TargetingLocationType | string;
  geocodeId?: LocationId | undefined;
  regionId?: LocationId | undefined;
  subRegionId?: LocationId | undefined;
  subdivision1Name?: string | undefined;
  subdivision2Name?: string | undefined;
  locationName?: string | undefined;
  tag?: string | undefined;
}

export interface GeolocationOption {
  cityId?: string | undefined;
  cityName?: string | undefined;
  code?: string | undefined;
  continentCode?: string | undefined;
  continentName?: string | undefined;
  countryIsoCode?: string | undefined;
  countryName?: string | undefined;
  geonameId?: string | undefined;
  isInEuropeanUnion?: boolean | undefined;
  metroCode?: string | undefined;
  name: string;
  subdivision1IsoCode?: string | undefined;
  subdivision1Name?: string | undefined;
  subdivision2IsoCode?: string | undefined;
  subdivision2Name?: string | undefined;
  timeZone?: string | undefined;
}

export interface ZipCodeTargeting {
  zipCode: string;
  excluded: boolean;
}

export interface IabCategoryTargeting {
  iabCategory: string;
  excluded: boolean;
}

export interface ContextualTargetingValue {
  name: string;
  excluded?: boolean | undefined;
}

export interface LiverampSegment {
  id: string;
  segmentId?: string | undefined;
  segmentName?: string | undefined;
  providerName?: string | undefined;
  description?: string | null | undefined;
  segmentDescription?: string | null | undefined;
  useRestrictions?: string | null | undefined;
  permittedAdvertisers?: string | null | undefined;
  customSegment?: boolean | undefined;
  digitalCpm?: number | null | undefined;
  tvCpm?: number | null | undefined;
}

export interface PersistedTargetingLiverampSegments {
  id?: string | undefined;
  excluded?: boolean | undefined;
  lrSegments?: unknown[] | undefined;
}

export interface PersistedTargeting {
  id?: string | undefined;
  name?: string | undefined;
  bundlesCount?: number | undefined;
  ipsCount?: number | undefined;
  deviceTypes?: PersistedTargetingDeviceType[] | undefined;
  platforms?: PersistedTargetingPlatform[] | undefined;
  geoLocations?: TargetingLocation<string | number | null>[] | undefined;
  zipCodes?: ZipCodeTargeting[] | undefined;
  supplyType?: TargetingSupplyType | string | undefined;
  includedApps?: unknown[] | undefined;
  excludedApps?: unknown[] | undefined;
  includedPlacements?: TargetingPlacement[] | undefined;
  excludedPlacements?: TargetingPlacement[] | undefined;
  placements?: (TargetingPlacement & { excluded?: boolean | undefined })[] | undefined;
  appStoreIds?: unknown[] | undefined;
  domains?: unknown[] | undefined;
  includedDomains?: unknown[] | undefined;
  excludedDomains?: unknown[] | undefined;
  includedTailoredSupplies?: unknown[] | undefined;
  excludedTailoredSupplies?: unknown[] | undefined;
  tailoredSupplyExcludedApps?: unknown[] | undefined;
  tailoredSupplyExcludedDomains?: unknown[] | undefined;
  iabCategories?: IabCategoryTargeting[] | undefined;
  viewability?: number | null | undefined;
  ctr?: number | null | undefined;
  supplyCompanies?: NamedTargetingEntity[] | undefined;
  userLanguages?: TargetingLanguage[] | undefined;
  includedChannels?: ContextualTargetingValue[] | undefined;
  excludedChannels?: ContextualTargetingValue[] | undefined;
  includedChannelGroups?: ContextualTargetingValue[] | undefined;
  excludedChannelGroups?: ContextualTargetingValue[] | undefined;
  includedContentTitles?: ContextualTargetingValue[] | undefined;
  excludedContentTitles?: ContextualTargetingValue[] | undefined;
  includedContentKeywords?: ContextualTargetingValue[] | undefined;
  excludedContentKeywords?: ContextualTargetingValue[] | undefined;
  includedContentGenres?: ContextualTargetingValue[] | undefined;
  excludedContentGenres?: ContextualTargetingValue[] | undefined;
  pmpDealIds?: unknown[] | undefined;
  includeBundles?: boolean | undefined;
  includeIps?: boolean | undefined;
  reqWithoutDeviceIfa?: boolean | null | undefined;
  reqWithoutAppStoreUrl?: boolean | null | undefined;
  reqFilterOutInstlNotEq1?: boolean | null | undefined;
  reqWithoutContentChannel?: boolean | null | undefined;
  reqWithoutContentNetwork?: boolean | null | undefined;
  xp1?: string | null | undefined;
  xp2?: string | null | undefined;
  xp3?: string | null | undefined;
  xp4?: string | null | undefined;
  xpExcluded?: boolean | undefined;
  targetingLRSegments?: unknown;
  customTargeting?: unknown;
  liverampTargeting?: unknown;
  /** Serialized line-item custom targeting rule builder (JSON string). */
  ext?: string | undefined;
}
